// =========================
// SIGN UP PAGE
// =========================
import App from './../../App';
import Auth from './../../Auth';
import { html, render } from 'lit-html';
import { anchorRoute, gotoRoute } from './../../Router';
import Utils from './../../Utils';

// This is the SignUpView class that handles the sign-up page
// It includes methods for rendering the page and handling user interactions
class SignUpView {
  constructor() {
    this.selectedRole = 'user';
    this.errorMessage = '';
    this.popupErrorMessage = '';
  }

  // This method initializes the view
  // It checks if the user is already logged in and redirects them if so
  init() {
    if (Auth.isLoggedIn()) {
      gotoRoute('/feed');
      return;
    }

    console.log('SignUpView.init');
    document.title = 'PopIn - Sign Up';
    this.render();
    Utils.pageIntroAnim();
  }

  // This method handles the sign-up form submission
  // It validates the input fields and calls the Auth.signUp method
  signUpSubmitHandler(e) {
    e.preventDefault();
    const form = e.target;
    const submitBtn = form.querySelector('.submit-btn');
    submitBtn.setAttribute('loading', '');

    const formData = new FormData(form);
    const password = formData.get('password');
    const confirmPassword = formData.get('confirmPassword');
    const requiredFields = [
      'firstName',
      'lastName',
      'email',
      'password',
      'confirmPassword',
    ];
    const missingFields = [];

    form.querySelectorAll('sl-input, sl-select').forEach((input) => {
      input.classList.remove('field-error');
    });

    requiredFields.forEach((name) => {
      const input = form.querySelector(`[name="${name}"]`);
      const value = formData.get(name);
      if (!value || value.trim() === '') {
        missingFields.push(name);
        if (input) input.classList.add('field-error');
      }
    });

    if (this.selectedRole === 'supplier') {
      const businessName = form.querySelector('[name="businessName"]');
      const contactEmail = form.querySelector('[name="contactEmail"]');
      const bNameVal = formData.get('businessName');
      const cEmailVal = formData.get('contactEmail');

      if (!bNameVal || bNameVal.trim() === '') {
        missingFields.push('businessName');
        businessName?.classList.add('field-error');
      }

      if (!cEmailVal || cEmailVal.trim() === '') {
        missingFields.push('contactEmail');
        contactEmail?.classList.add('field-error');
      }
    }

    const confirmPasswordInput = form.querySelector('[name="confirmPassword"]');
    if (password && confirmPassword && password !== confirmPassword) {
      missingFields.push('passwordMismatch');
      confirmPasswordInput?.classList.add('field-error');
    }

    if (missingFields.length > 0) {
      this.errorMessage =
        missingFields.length === 1 && missingFields[0] === 'passwordMismatch'
          ? 'Passwords do not match.'
          : missingFields.length === 1
            ? `${Utils.formatFieldName(missingFields[0])} is required.`
            : 'Multiple fields are incorrect.';
      submitBtn.removeAttribute('loading');
      this.render();
      return;
    }

    formData.delete('confirmPassword');
    this.errorMessage = '';

    Auth.signUp(formData, () => {
      submitBtn.removeAttribute('loading');
    }).catch((err) => {
      submitBtn.removeAttribute('loading');
      this.errorMessage = err.message || 'Something went wrong.';
      this.render();
    });
  }

  // This method handles the login form submission
  // It calls the Auth.signIn method with the form data
  logInSubmitHandler(e) {
    e.preventDefault();
    const form = e.target;
    const submitBtn = form.querySelector('.submit-btn');
    submitBtn.setAttribute('loading', '');

    const formData = new FormData(form);

    Auth.signIn(formData)
      .then(() => {
        submitBtn.removeAttribute('loading');
        this.popupErrorMessage = '';
        document.getElementById('loginPopup').active = false;
      })
      .catch((err) => {
        submitBtn.removeAttribute('loading');
        this.popupErrorMessage = 'Invalid email or password.';
        this.render();
      });
  }

  // This method toggles the login popup visibility
  toggleLoginPopup() {
    const popup = document.getElementById('loginPopup');
    this.popupErrorMessage = '';
    this.render();

    popup.placement = window.innerWidth <= 1024 ? 'bottom' : 'bottom-start';
    popup.active = !popup.active;
  }

  // This method handles the role selection change
  // It updates the selected role and re-renders the view
  handleRoleChange(e) {
    this.selectedRole = e.target.value;
    this.render();
  }

  render() {
    const template = html`
      <nav>
        <div>
          <a href="/" @click=${anchorRoute}><img src="images/logo.svg" /></a>
        </div>
        <div>
          <sl-button
            variant="primary"
            id="loginToggleBtn"
            @click=${this.toggleLoginPopup.bind(this)}
            >Log In</sl-button
          >

          <sl-popup
            class="login-popup"
            id="loginPopup"
            anchor="loginToggleBtn"
            placement="bottom-start"
            arrow
          >
            <form id="loginForm" @submit=${this.logInSubmitHandler.bind(this)}>
              ${this.popupErrorMessage
                ? html`
                    <sl-alert variant="danger" open closable>
                      <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
                      <span>${this.popupErrorMessage}</span>
                    </sl-alert>
                  `
                : ''}
              <sl-input
                name="email"
                type="email"
                placeholder="Email"
                required
              ></sl-input>
              <sl-input
                name="password"
                type="password"
                placeholder="Password"
                required
              ></sl-input>
              <sl-button
                type="submit"
                variant="neutral"
                class="submit-btn"
                variant="primary"
                >Log In</sl-button
              >
            </form>
          </sl-popup>

          <sl-button
            variant="default"
            outline
            class="anim-in"
            @click=${() => gotoRoute('/signup')}
            >Sign Up</sl-button
          >
        </div>
      </nav>

      <div id="landing-signinup">
        <div id="hero-left" class="page-centered">
          <div class="signinup-box">
            <h1>Sign Up</h1>
            ${this.errorMessage
              ? html`
                  <sl-alert
                    variant="danger"
                    class="signup-sections"
                    open
                    closable
                  >
                    <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
                    <span .innerHTML=${this.errorMessage}></span>
                  </sl-alert>
                `
              : ''}
            <form
              class="form-signup"
              @submit=${this.signUpSubmitHandler.bind(this)}
            >
              <div class="input-group signup-sections">
                <sl-select
                  name="role"
                  value=${this.selectedRole}
                  required
                  @sl-change=${this.handleRoleChange.bind(this)}
                >
                  <sl-option value="user">User</sl-option>
                  <sl-option value="supplier">Supplier</sl-option>
                </sl-select>
              </div>
              <div class="input-group">
                <sl-input
                  name="firstName"
                  type="text"
                  placeholder="First Name"
                  required
                ></sl-input>
              </div>
              <div class="input-group">
                <sl-input
                  name="lastName"
                  type="text"
                  placeholder="Last Name"
                  required
                ></sl-input>
              </div>
              <div class="input-group">
                <sl-input
                  name="email"
                  type="email"
                  placeholder="Email"
                  required
                ></sl-input>
              </div>
              <div class="input-group">
                <sl-input
                  name="password"
                  type="password"
                  placeholder="Password"
                  required
                  toggle-password
                ></sl-input>
              </div>
              <div class="input-group signup-sections">
                <sl-input
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm Password"
                  required
                  toggle-password
                ></sl-input>
              </div>

              ${this.selectedRole === 'supplier'
                ? html`
                    <div class="signup-sections">
                      <div class="input-group">
                        <sl-input
                          name="businessName"
                          type="text"
                          placeholder="Business Name"
                          required
                        ></sl-input>
                      </div>
                      <div class="input-group">
                        <sl-input
                          name="contactEmail"
                          type="email"
                          placeholder="Contact Email"
                          required
                        ></sl-input>
                      </div>
                    </div>
                  `
                : ''}

              <sl-button
                type="submit"
                variant="primary"
                class="submit-btn"
                style="width: 100%;"
                >Sign Up</sl-button
              >
            </form>
            <p>
              Have an account?
              <a href="/signin" @click=${anchorRoute}>Sign In</a>
            </p>
          </div>
        </div>

        <div id="hero-right">
          <img src="images/hero.png" />
        </div>
      </div>

      <footer>
        <div>
          <img src="images/icon.svg" />
        </div>
        <p>
          This website is the official online presence of PopIn. All content,
          including images and text, is the property of PopIn or its respective
          owners and is used with permission. Unauthorized reproduction or
          distribution of any material on this site is prohibited. For inquiries
          regarding content usage, please contact us directly.
        </p>
        <div>
          <sl-icon src="images/square-instagram-brands.svg"></sl-icon>
          <sl-icon src="images/square-facebook-brands.svg"></sl-icon>
          <sl-icon src="images/square-tiktok-brands.svg"></sl-icon>
          <sl-icon src="images/square-x-twitter-brands.svg"></sl-icon>
          <sl-icon src="images/square-linkedin-brands.svg"></sl-icon>
        </div>
      </footer>
    `;
    render(template, App.rootEl);
  }
}

export default new SignUpView();
